import React, { forwardRef, useId } from "react";
import clsx from "clsx";
import styles from "./Radio.module.scss";
import type { RadioProps } from "./Radio.types";

/**
 * Radio component
 *
 * A coustomizable radio button with support for:
 * -Multiple sizes (sm,md,lg)
 * -Multiple colors
 * -Labels and description
 * -Disabled and error state
 * -Custom Colors
 *
 * @example
 * <Radio name='option' value='1' label='option1'/>
 * * @example
 * // With description
 * <Radio
 *   name="plan"
 *   value="pro"
 *   label="Pro Plan"
 *   description="$29/month, unlimited features"
 * />
 *
 * @example
 * // Controlled
 * <Radio
 *   name="choice"
 *   value="yes"
 *   isChecked={selected === 'yes'}
 *   onChange={() => setSelected('yes')}
 *   label="Yes"
 * />
 */

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      //Content
      label,
      description,
      labelPosition = "right",
      //Styling
      size = "md",
      color = "primary",
      varient = "filled",
      //State
      isChecked,
      isDisabled = false,
      isError = false,
      // Visibility
      isRadioBtnShown = true, //  Default to true
      //Custom colors
      checkedColor,
      borderColor,
      //Html attributes
      className,
      id,
      checked,
      disabled,
      onChange,
      ...rest
    },
    ref,
  ) => {
    // Generate unique id
    const generatedId = useId();
    const radioId = id || generatedId;

    //Computed Values
    //Use isChecked prop or fallback to checked attribute
    const isRadioChecked = isChecked !== undefined ? isChecked : checked;

    //Use isDisabled prop or fallback to disabled attribute
    const isRadioDisabled = isDisabled || disabled;

    //Css custom variables
    const customStyles = {
      ...(checkedColor && { "--radio-checked-color": checkedColor }),
      ...(borderColor && { "--radio-border": borderColor }),
    } as React.CSSProperties;

    const hasCustomStyle = checkedColor || borderColor;

    // Wrapper classes (contains radio + label)
    const wrapperClasses = clsx(
      styles.radioWrapper,
      styles[`size${size}`],
      {
        [styles.labelLeft]: labelPosition === "left",
        [styles.labelRight]: labelPosition === "right",
        [styles.isDisabled]: isDisabled,
        [styles.isError]: isError,
        [styles.hasDescription]: Boolean(description),
        [styles.radioHidden]: !isRadioBtnShown, // 👈 New class
        [styles.isChecked]: isRadioChecked && !isRadioBtnShown, // 👈 For styling when radio hidden
      },
      className,
    );

    // Radio input classes
    const radioClasses = clsx(
      styles.radio,
      styles[`radio-${size}`],
      styles[`${varient}-${color}`],
      {
        [styles.hasCustomStyle]: hasCustomStyle,
        [styles.visuallyHidden]: !isRadioBtnShown, // 👈 Hide visually but keep accessible
      },
    );
    const labelClasses = clsx(styles.label);
    const descriptionClasses = clsx(styles.description);

    return (
      <label
        className={wrapperClasses}
        style={hasCustomStyle ? customStyles : undefined}
        htmlFor={radioId}
      >
        {/**
         * Radio Input
         * The actual input element (hidden,replaced by styled element)
         */}
        <input
          ref={ref}
          type="radio"
          id={radioId}
          className={radioClasses}
          checked={isRadioChecked}
          disabled={isRadioDisabled}
          aria-invalid={isError}
          onChange={onChange}
          {...rest}
        />

        {/* 
            LABEL & DESCRIPTION
            Only rendered if label is provided
            */}
        {(label || description) && (
          <div className={styles.labelContainer}>
            {label && <span className={labelClasses}>{label}</span>}
            {description && (
              <span className={descriptionClasses}>{description}</span>
            )}
          </div>
        )}
      </label>
    );
  },
);

Radio.displayName = "Radio";
